import type { Identifier } from "ra-core";

import type { SalesTool, SalesToolKind } from "../types";

/** Display order of the tool kinds; also the first tie-break of the relevance order. */
export const SALES_TOOL_KINDS: SalesToolKind[] = [
  "script",
  "question",
  "objection",
  "video",
  "brochure",
  "comparison",
  "fact_sheet",
];

/** Library filters; an empty string means "no filter". */
export type SalesToolFilters = {
  kind: string;
  stage: string;
  projectId: string;
  buyerTypeId: string;
  search: string;
  showArchived: boolean;
};

export const EMPTY_TOOL_FILTERS: SalesToolFilters = {
  kind: "",
  stage: "",
  projectId: "",
  buyerTypeId: "",
  search: "",
  showArchived: false,
};

/** The lead attributes the relevance order depends on. */
export type LeadToolContext = {
  stage: string;
  projectId?: Identifier | null;
};

/** An empty tag array means "applies to all". */
const tagMatches = <T extends string | number>(
  tags: T[] | null | undefined,
  value: string,
): boolean => !tags?.length || tags.some((tag) => String(tag) === value);

/**
 * Library filter predicate. A tool with an empty tag array appears under every
 * stage / project / client-type filter. Archived tools only pass when
 * `showArchived` is set (the UI only offers that to direccion / lead_manager).
 */
export const matchesToolFilters = (
  tool: SalesTool,
  filters: SalesToolFilters,
): boolean => {
  if (tool.archived_at && !filters.showArchived) return false;
  if (filters.kind && tool.kind !== filters.kind) return false;
  if (filters.stage && !tagMatches(tool.stage_keys, filters.stage)) {
    return false;
  }
  if (filters.projectId && !tagMatches(tool.project_ids, filters.projectId)) {
    return false;
  }
  if (
    filters.buyerTypeId &&
    !tagMatches(tool.buyer_type_ids, filters.buyerTypeId)
  ) {
    return false;
  }
  const search = filters.search.trim().toLocaleLowerCase("es");
  if (search) {
    const haystack = `${tool.title} ${tool.body ?? ""}`.toLocaleLowerCase("es");
    if (!haystack.includes(search)) return false;
  }
  return true;
};

/** Number of tiers; a tool at `NOT_RELEVANT_RANK` does not apply to the lead. */
export const NOT_RELEVANT_RANK = 4;

/**
 * Relevance tier for a lead: 0 stage + project both tagged, 1 stage-only
 * (no project tag), 2 project-only (no stage tag), 3 untagged (applies to
 * all), 4 the rest. Uses tags only: never usage, popularity or advisor data.
 */
export const relevanceRank = (
  tool: SalesTool,
  lead: LeadToolContext,
): number => {
  const stageTags = tool.stage_keys ?? [];
  const projectTags = tool.project_ids ?? [];
  const stageHit = stageTags.some((key) => key === lead.stage);
  const projectHit =
    lead.projectId != null &&
    projectTags.some((id) => String(id) === String(lead.projectId));

  if (stageHit && projectHit) return 0;
  if (stageHit && projectTags.length === 0) return 1;
  if (projectHit && stageTags.length === 0) return 2;
  if (stageTags.length === 0 && projectTags.length === 0) return 3;
  return NOT_RELEVANT_RANK;
};

const compareTools = (a: SalesTool, b: SalesTool): number =>
  SALES_TOOL_KINDS.indexOf(a.kind) - SALES_TOOL_KINDS.indexOf(b.kind) ||
  a.title.localeCompare(b.title, "es") ||
  String(a.id).localeCompare(String(b.id));

/**
 * Orders the non-archived tools for a lead (rank, then kind, then title, then
 * id: fully deterministic). Tools at `NOT_RELEVANT_RANK` are kept, last, so the
 * caller decides whether to show them.
 */
export const orderToolsForLead = (
  tools: SalesTool[],
  lead: LeadToolContext,
): SalesTool[] =>
  tools
    .filter((tool) => !tool.archived_at)
    .map((tool) => ({ tool, rank: relevanceRank(tool, lead) }))
    .sort((a, b) => a.rank - b.rank || compareTools(a.tool, b.tool))
    .map(({ tool }) => tool);

/** Only the tools that apply to the lead (ranks 0 to 3), best first. */
export const relevantToolsForLead = (
  tools: SalesTool[],
  lead: LeadToolContext,
): SalesTool[] =>
  orderToolsForLead(tools, lead).filter(
    (tool) => relevanceRank(tool, lead) < NOT_RELEVANT_RANK,
  );
